import React, {useEffect, useMemo, useRef, useState} from "react";
import * as PIXI from 'pixi.js';
import {TwistFilter, PixelateFilter, AsciiFilter, BloomFilter, DotFilter, GlitchFilter, RGBSplitFilter} from "pixi-filters";
import {urlToObjectUrl} from "../utils";
import useTranslation from "../../virtual_congress/effects/use_translation";

const sepiaFilter = new PIXI.filters.ColorMatrixFilter()
sepiaFilter.sepia(true)

const greyScaleFilter = new PIXI.filters.ColorMatrixFilter()
greyScaleFilter.greyscale(0.4, true)

const FILTERS = {
  blur: {
    key: "blur",
    title: "Blur",
    filter: new PIXI.filters.BlurFilter(),
  },
  twist: {
    key: "twist",
    title: "Twist",
    filter: new TwistFilter(),
  },
  sepia: {
    key: "sepia",
    title: "Sepia",
    filter: sepiaFilter,
  },
  pixelate: {
    key: "pixelate",
    title: "Pixelate",
    filter: new PixelateFilter(),
  },
  black_and_white: {
    key: "black_and_white",
    title: "Black & White",
    filter: greyScaleFilter,
  },
  ascii: {
    key: "ascii",
    title: "Ascii",
    filter: new AsciiFilter(),
  },
  bloom: {
    key: "bloom",
    title: "Bloom",
    filter: new BloomFilter(),
  },
  dot: {
    key: "dot",
    title: "Dot",
    filter: new DotFilter(),
  },
  glitch: {
    key: "glitch",
    title: "Glitch",
    filter: new GlitchFilter(),
  },
  rgb_split: {
    key: "rgb_split",
    title: "Rgb Split",
    filter: new RGBSplitFilter(),
  },
}

const PixiEditor = ({
                      imageBlob,
                      onFileEdited,
                      photoBoothConfig,
                      selectedFrame,
                      showBackgrounds,
                      showFilters,
                      removeFrameFromFinalImage,
                      onResetClicked,
                      onBackClicked,
                      translationHtmlContent,
                    }) => {
  const t = useTranslation(translationHtmlContent)
  const [frameSprite, setFrameSprite] = useState()
  const [backgroundSprite, setBackgroundSprite] = useState()
  const [sprite, setSprite] = useState()
  const [container, setContainer] = useState()
  const [availableFilters, setAvailableFilters] = useState([])
  const pixiContainer = useRef(null);
  const backgrounds = photoBoothConfig.backgrounds.map(e => {
    return {
      key: e.id,
      src: e.url
    }
  })

  useEffect(() => {
    const tmpAvailableFilters = []
    if (photoBoothConfig.blur_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.blur)
    }
    if (photoBoothConfig.twist_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.twist)
    }
    if (photoBoothConfig.sepia_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.sepia)
    }
    if (photoBoothConfig.pixelate_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.pixelate)
    }
    if (photoBoothConfig.black_and_white_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.black_and_white)
    }
    if (photoBoothConfig.ascii_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.ascii)
    }
    if (photoBoothConfig.bloom_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.bloom)
    }
    if (photoBoothConfig.dot_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.dot)
    }
    if (photoBoothConfig.glitch_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.glitch)
    }
    if (photoBoothConfig.rgb_split_filter_enabled) {
      tmpAvailableFilters.push(FILTERS.rgb_split)
    }

    setAvailableFilters(tmpAvailableFilters)
  }, [photoBoothConfig])

  const app = useMemo(() => {
    return new PIXI.Application({
      width: photoBoothConfig.target_image_width,
      height: photoBoothConfig.target_image_height,
    });
  }, [])

  useEffect(() => {
    if (app && pixiContainer.current) {
      pixiContainer.current.appendChild(app.view)

      // Add background
      let backgroundSprite = new PIXI.Sprite();

      // Add frame
      const frameImage = new Image();
      frameImage.crossOrigin = "Anonymous";
      frameImage.src = selectedFrame;
      const frameBaseTexture = new PIXI.BaseTexture(frameImage)
      const frameTexture = new PIXI.Texture(frameBaseTexture);
      const frameSprite = new PIXI.Sprite(frameTexture);
      frameSprite.zIndex = 1000
      frameImage.addEventListener("load", function (event) {
        frameSprite.width = photoBoothConfig.target_image_width;
        frameSprite.height = photoBoothConfig.target_image_height;
      });

      // Add picture
      const blobURL = URL.createObjectURL(imageBlob);
      const image = new Image();
      image.src = blobURL;
      const baseTexture = new PIXI.BaseTexture(image)
      const texture = new PIXI.Texture(baseTexture);
      const sprite = new PIXI.Sprite(texture);
      sprite.zIndex = 100
      image.addEventListener("load", function (event) {
        sprite.width = photoBoothConfig.target_image_width;
        sprite.height = photoBoothConfig.target_image_height;
      });

      const container = new PIXI.Container()

      app.stage.addChild(container);

      container.addChild(backgroundSprite);
      container.addChild(sprite);
      container.addChild(frameSprite);

      setBackgroundSprite(backgroundSprite)
      setFrameSprite(frameSprite)
      setSprite(sprite)
      setContainer(container)

      if (backgrounds.length > 0) {
        onSelectedBackground(backgrounds[0])();
      }

    }
  }, [app, pixiContainer.current])

  const onFilterSelected = (filter) => {
    return () => {
      if (sprite) {
        if (filter === "none") {
          sprite.filters = []
        } else {
          sprite.filters = [filter.filter]
        }
      }
    }
  }

  const onDoneClicked = () => {
    if (removeFrameFromFinalImage) {
      container.removeChild(frameSprite);
    }
    app.renderer.plugins.extract.canvas(container).toBlob(blob => {
      onFileEdited(blob)
    }, 'image/png');
  }

  const onSelectedBackground = (background) => {
    return () => {
      urlToObjectUrl(background.src).then(url => {
        const image = new Image();
        image.crossOrigin = "Anonymous";
        image.src = url
        const baseTexture = new PIXI.BaseTexture(image)
        const texture = new PIXI.Texture(baseTexture);
        backgroundSprite.texture = texture
        backgroundSprite.zIndex = 10
        image.addEventListener("load", function (event) {
          backgroundSprite.width = photoBoothConfig.target_image_width;
          backgroundSprite.height = photoBoothConfig.target_image_height;
        });
        // No idea if this is bad practice to re add them always
        // But since there is no real solution to update sprites and this works ...
        container.addChild(backgroundSprite);
        container.addChild(sprite);
        container.addChild(frameSprite);
      })
    }
  }

  return <div className={"pixi-container"}>
    <div className={"pixi-canvas"} ref={pixiContainer}/>

    {showFilters && <div className={"filters-container"}>
      {availableFilters.map(filter => {
        return <button
          className={"filter-button photo-booth-button"}
          key={filter.key}
          type={"button"}
          onClick={onFilterSelected(filter)}
        >
          {t(`photo_booth.pixi.filters.${filter.key}`, {}, filter.title)}
        </button>
      })}
      <button
        type={"button"}
        className={"filter-button photo-booth-button"}
        onClick={onFilterSelected("none")}
      >
        {t(`photo_booth.pixi.filters.none`, {}, "None")}
      </button>
    </div>}

    {showBackgrounds && <div
      className={"backgrounds-container"}
    >
      {backgrounds.map(background => {
        return <img
          className={"background-button asset-button"}
          onClick={onSelectedBackground(background)}
          style={{width: "150px"}}
          key={background.key}
          src={background.src}/>
      })}
    </div>}

    <div className={"button-container"}>
      <button
        className={"photo-booth-button back-button"}
        onClick={onBackClicked}
        type={"button"}>
        {t("photo_booth.pixi.back_button_html", {}, "Back")}
      </button>

      <button
        className={"photo-booth-button reset-button"}
        onClick={onResetClicked}
        type={"button"}>
        {t("photo_booth.pixi.reset_button_html", {}, "Reset")}
      </button>

      <button
        className={"photo-booth-button finished-button"}
        type={"button"}
        onClick={onDoneClicked}
      >
        {t(`photo_booth.pixi.continue_button_html`, {}, "Done")}
      </button>
    </div>
  </div>
}

export default PixiEditor